<template>
  <div class="agf--flow-page--body" :class="bodyClasses">
    <component :is="bodyContent" />
  </div>
</template>
<script lang="ts">
import { PropType } from 'vue'
import { ModelPsFlowPages } from '../../models/fireModels/types'
import { parsePsAgreementEmbeddableValue } from '../../services/PsAgreementEmbeddableTemplateParser/psAgreementEmbeddableTemplateDef'
import { AgreementFlowDefinitionValueTemplateUserDefineItem } from '../AgreementFlowDefinitionInput/AgreementFlowDefinitionInputService'
import { AgreementFlowService } from './AgreementFlowService'
import FlowPageUserInput from './FlowPageUserInput.vue'

function stripTags(str) {
  return str.replace(/<\/?[^>]+(>|$)/g, '')
}
/**
 * 1. Replace {{key}} with flowVariables[key].value
 * 2. Return Vue renderable content
 * - `[[[[user-input type="checkbox" key="確認1" required="true" label="当院より、動物の退院や引き取りの連絡があった場合には、速やかに引き取る旨、説明を受け、了解しました。"]]]]` の形式を Vue renderable tag に変換する
 * @param content
 * @param flowVariables
 */
const formatFlowPageContent = (
  content: string,
  flowVariables: Record<string, AgreementFlowDefinitionValueTemplateUserDefineItem>,
) => {
  return content
    .replace(/{{([^}]+)}}/g, (_, key) => {
      key = stripTags(key || '')?.trim() || ''
      const content = (flowVariables[key]?.value || flowVariables[key] || '　　　　　　　　　')
      if (typeof content === 'string' && typeof content?.indexOf === 'function' && content?.indexOf('\n') !== -1) {
        return content.replace(/\n/g, '<br/>')
      }
      return content
    })
    .replace(/\[\[\[\[user-input (.*?)\]\]\]\]/g, '<user-input $1></user-input>')
}

// 吹き出し型, カード型, 枠なし, モーダル
const classNameByTypes = {
  吹き出し型: 'balloon',
  カード型: 'card',
  枠なし: 'no-frame',
  モーダル: 'modal',
}

export default {
  name: 'FlowPageBody',
  inject: ['AgreementFlowService'],
  components: {
    FlowPageUserInput,
  },
  props: {
    pageIndex: {
      type: Number,
      required: true,
    },
    flowPage: {
      type: Object as PropType<ModelPsFlowPages>,
      required: true,
    },
    flowVariables: {
      type: Object as PropType<
        Record<string, AgreementFlowDefinitionValueTemplateUserDefineItem>
      >,
      required: true,
    },
  },
  computed: {
    sv(): AgreementFlowService {
      return this.AgreementFlowService as AgreementFlowService
    },
    flowPageContentVariablesVueRenderableReplaced() {
      return (
        this.flowPage?.content?.replace(/{{([^}]+)}}/g, (_, key) => {
          return `{{flowVariables.${key}}}`
        }) || ''
      )
    },
    flowVariablesKeyValues() {
      const ps_clinics = this.sv.agreementVariables?.agreementRequest?.clinic_id?.id
        ? this.sv.agreementVariables?.agreementRequest?.clinic_id
        : {}
      const flowVariables = parsePsAgreementEmbeddableValue({
        ps_clinics,
        ps_agreement_requests: this.sv.agreementVariables?.agreementRequest || {},
      })
      return Object.keys(this.flowVariables).reduce(
        (acc, key) => {
          acc[key] = this.flowVariables[key].value
          return acc
        },
        {
          ...flowVariables,
        },
      )
    },
    bodyContent() {
      const flowVariables = this.flowVariablesKeyValues
      const content = formatFlowPageContent(this.flowPage.content || '', flowVariables)
      return {
        template: `<div>${content}</div>`,
        components: {
          UserInput: FlowPageUserInput,
        },
        computed: {
          flowVariables() {
            return flowVariables || {}
          },
        },
      }
    },
    bodyClasses() {
      const typeClass = `agf--flow-page--body--${classNameByTypes[
        this.flowPage?.display_type || ''
      ] || 'none'}`
      return {
        [typeClass]: true,
      }
    },
  },
}
</script>
